import { type RefObject, useEffect, useRef } from 'react';

import { ABOVE_MODALS_ATTRIBUTE } from './above-modals';

/**
 * Every body child but the overlay and those marked above modals (the toaster) goes inert while the overlay is
 * open, as outside a native modal dialog, which would also cover the toasts. Focus moves to the heading and
 * returns to the opener on close. Keydown stops at the window in its capture phase, wherever focus is, so no
 * listener under the overlay sees a key; default actions such as Tab and button activation still run, and
 * Escape calls `onEscape`. A closing overlay turns `active` off and releases all of it at once, before its fade ends.
 */
export const useModalLayer = (
  overlayRef: RefObject<HTMLElement | null>,
  headingRef: RefObject<HTMLElement | null>,
  onEscape: () => void,
  active: boolean
) => {
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const background = [...document.body.children].filter(
      (element) =>
        element !== overlayRef.current && !element.hasAttribute(ABOVE_MODALS_ATTRIBUTE) && !element.hasAttribute('inert')
    );
    background.forEach((element) => element.setAttribute('inert', ''));
    headingRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === 'Escape') onEscapeRef.current();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });

    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      background.forEach((element) => element.removeAttribute('inert'));
      opener?.focus();
    };
  }, [overlayRef, headingRef, active]);
};
